<template>
    <div class="leftCardContainer">
        <loading-spinner :active.sync="submitLoading" :is-full-page="true" color="var(--app-primary-base)" />
        <div class="rightSideTitleActions">
            <span class="leftTitle">{{ $t('Validate') }}</span>
            <div class="actionsContainer" v-show="conflicts.length > 0" :style="{visibility: activeTab!==0 ? 'hidden' : 'visible'}">
                <div class="searchContainer">
                    <v-icon> search </v-icon>
                    <InputField
                        inputType="text"
                        :value="search"
                        :placeholder="$t('Search')"
                        :inputStyle="{
                            borderRadius: '0px',
                            width: '130px',
                            borderTop: 'none',
                            borderRight: 'none',
                            borderLeft: 'none',
                            borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
                        }"
                        @input="handleSearch($event)"
                    />
                </div>
            </div>
        </div>
        <div class="tabsContainer" v-show="hasImportSelected">
            <Tabs
                ref="conflictsTable"
                :activeTab="activeTab"
                :tabs="tabs"
                :centerTabs="false"
                :tabs-styling="tabsStyling"
                :tabs-labels-styling="tabsLabelsStyling"
                @startTabChange="setActiveTab"
            />
        </div>
        <div :style="{ height: hasImportSelected ? 'calc(92% - 86px)' : '100%' }">
            <div class="empty-table" v-if="!hasImportSelected">
                <p>{{ $t('ImportsMessage') }}</p>
            </div>
            <Table
                v-show="conflicts.length > 0 && !isLastTab"
                :scroll-id="tableId"
                :checkbox="false"
                :loading="tableLoading"
                :headers="headers"
                :items="conflicts"
                :sortable="true"
                :emitRowClickBusEvent="false"
                :conflicts="true"
                @selected-conflicts="handleSelectedConflicts"
                @onScrollBottom="onScrollBottom"
                @onScrollTop="onScrollTop"
                @sortClick="onSortClick"
            />
            <imports-summary v-show="hasImportSelected && isLastTab" :fields="summaryFields"/>
        </div>
        <div v-if="hasImportSelected" class="action-buttons">
            <custom-button
                :label="$t('Cancel')"
                type="cancel"
                :button-color="'#fff'"
                :label-color="'var(--app-primary-base)'"
                :button-styling="actionButtonsStylingSave"
                @buttonClick="clearTable"
            />
            <custom-button
                :is-loading="submitLoading"
                :label="submitButtonLabel"
                :button-color="'var(--app-primary-base)'"
                :label-color="'#FFF'"
                :disabled="!showValidate"
                @buttonClick="submitHandler"
            />
        </div>
        <LoadingSpinner :active.sync="loading" :is-full-page="true" color="var(--app-primary-base)" />
    </div>
</template>

<script>
    import { mapActions } from "vuex";
    import { bus } from "@/main.js";
    import Table from "@/newComponents/_Molecules/Table.vue";
    import InputField from "@/newComponents/_Atoms/InputField.vue";
    import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
    import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
    import CustomButton from '@/newComponents/_Atoms/CustomButton';
    import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';
    import ImportsSummary from "@/newComponents/Data/Imports/ImportsSummary.vue";
    import Tabs from "@/newComponents/_Atoms/Tabs.vue";
    import LoadingSpinner from "vue-loading-overlay";

    export default {
        name: "TableViewConflicts",

        mixins: [managementDatatableMixin],

        components: {
            Table,
            InputField,
            DialogContent,
            CustomButton,
            DialogChoice,
            Tabs,
            ImportsSummary,
            LoadingSpinner
        },

        props: {
            importObj: {
                type: Object
            },
            layers: {
                type: Array
            }
        },

        watch: {
            importObj: {
                handler(newValue, oldValue) {
                    if (Object.entries(newValue).length > 0 && oldValue.id !== newValue.id) {
                        if (this.tabs.length === 2) {
                            this.tabs.shift();
                            this.resetToDefault();
                        }
                    }
                },
                deep: true
            },
            conflicts(newValue){
                if(newValue.length > 0){
                    bus.$emit('draw-features', newValue.map(item => item.geo_json));
                }else{
                    bus.$emit('draw-features-highlight', []);
                }
            },
        },

        computed: {
            headers() {
                return [
                    { text: 'ID', varName: "id" },
                    { text: 'UUID', varName: "uuid" },
                    { text: this.$i18n.tc("Label", 1) , varName: "label" },
                ]
            },

            hasImportSelected(){
                return Object.entries(this.importObj).length !== 0;
            },

            showValidate(){
                // Disable button if there are no conflicts
                if (this.conflicts.length === 0 && this.importObj.finalized_at !== null) {
                    return false;
                }
                //if user does not has permissions then disable button
                if(!this.userHasPermissionToValidateAndStore()){
                    return true;
                }
                if(Object.entries(this.importObj).length !== 0){
                    //if it has only one tab, show buttons when it is success
                    if(this.tabs.length === 1){
                        if((this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects === 0) || this.importObj.step === 'import_to_database'){
                            return true;
                        }
                    }else{
                        //if it has two tabs, if user is in last tab, check for conditions, if user is in initial tab, always show buttons
                        if(this.tabs.length === (this.activeTab + 1)){
                            if(this.selectedConflicts.length === 0){
                                return false;
                            }else if(this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects !== 0 && this.importObj.total_objects && this.tabs.length > 1){
                                return true;
                            }else if((this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects === 0) || this.importObj.step === 'import_to_database'){
                                return true;
                            }else{
                                return false;
                            }
                        }else{
                            return true;
                        }
                    }
                }
                return false;
            },

            isLastTab(){
                return this.tabs.length === (this.activeTab + 1);
            },

            submitButtonLabel(){
                return this.tabs.length === (this.activeTab + 1) ? this.$i18n.t('Submit') : this.$i18n.t('Next');
            },

            summaryFields() {
                let fields = [
                    {
                        icon: "person",
                        label: this.$i18n.tc("User", 1),
                        value: this.importObj.user_name,
                        size: '13px',
                    },
                    {
                        icon: "label",
                        label: this.$i18n.tc("Layer", 1),
                        value: this.importObj.type,
                        size: '13px',
                        pngSrc: null
                    },
                    {
                        icon: null,
                        label: this.$i18n.tc("Projection", 1),
                        value: this.importObj.projection,
                        size: null,
                        pngSrc: require('@/assets/importsIcons/world.png'),
                        width: '13px',
                        height: '13px'
                    },
                    {
                        icon: "label",
                        label: this.$i18n.tc("Template", 1),
                        value: this.importObj.template_id,
                        size: '13px',
                        pngSrc: null
                    },
                    {
                        icon: null,
                        label: `${this.$i18n.tc("File", 1)} ${this.$i18n.t("Name")}`,
                        value: this.importObj.original_file_name,
                        size: null,
                        pngSrc: require('@/assets/importsIcons/file_document.png'),
                        width: '12px',
                        height: '12px'
                    },
                    /*
                    {
                        icon: "cloud_upload",
                        label: `${this.$i18n.t("Imported")} ${this.$i18n.tc("Object", 2)}`,
                        value: '',
                        size: '13px',
                        pngSrc: null
                    },
                    */
                ];
                return fields;
            },
        },

        data() {
            return {
                tabs: [
                    { label: this.$i18n.t("Summary") },
                ],
                tabsStyling: {
                    'margin': '0px 0px 8px 0px',
                    'width': '100%',
                    'height': '32px',
                },
                tabsLabelsStyling: {
                    'text-transform': 'none',
                    'font-size': '10px',
                    'letter-spacing': '0',
                },
                actionButtonsStylingSave: {
                    'box-sizing': 'border-box',
                    'border': '1px solid var(--app-primary-base)',
                    'padding': '5px 19px'
                },
                tableId: '2',
                search: null,
                tableLoading: false,
                loading:false,
                submitLoading: false,
                scrollTop: 0,
                sortOrder: null,
                scrollHeight: 0,
                pageNumber: 1,
                pagination: 15,
                currentPageTop: 1,
                currentPageBot: 1,
                lastPage: 2,
                conflicts: [],
                activeTab: 0,
                selectedConflicts: [],
                originalResponse: []
            };
        },

        methods: {
            ...mapActions(["GET_IMPORT_CONFLICTS", "CREATE_IMPORT"]),

            /**
             * Checks if the user has permission to validate/try to generate an import of a specific layer.
             * @returns {Boolean}
             */
            userHasPermissionToValidateAndStore() {
                if (this.$store.getters.layerExistsInObjectForms(this.importObj.filteredType)) {
                    let permissionName = this.$store.getters.getLayerPermissionName(this.importObj.filteredType);
                    return this.userHasManagementPermission(permissionName, "import_store");
                }
                return true;
            },

            handleValidate(){
                if(this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects !== 0 && this.importObj.total_objects){
                    if(this.tabs.length < 2){
                        this.tabs[0].label = this.$i18n.tc("Conflict", 2);
                        this.tabs.push({ label: this.$i18n.t("Summary") });
                    }
                    this.getInitialConflicts();
                }
            },

            submitHandler(){
                if((this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects === 0) || this.importObj.step === 'import_to_database'){
                    //objects does not have conflicts, then create objects
                    this.getConflicts();
                }else if(this.importObj.step === 'save_temporary_table' && this.importObj.total_conflicted_objects !== 0 && this.importObj.total_objects){
                    if(this.tabs.length !== (this.activeTab + 1)){
                        this.activeTab = this.activeTab + 1;
                        return;
                    }
                    this.resolveConflicts();
                }
            },

             async getConflicts() {
                try {
                    this.submitLoading = true;
                    let layer = this.layers.find(layer => layer.layer === this.importObj.filteredType);
                    await this.CREATE_IMPORT({
                        endpoint: layer.endpoint,
                        data: {
                            import_uuid: this.importObj.uuid,
                        },
                    });
                    this.$emit('refresh-imports-table');
                    this.clearTable();
                } catch (error) {
                    console.error(error);
                    this.$toasted.global.toast_error(this.$i18n.t("ConflictsNotResolverMsg"));
                } finally {
                    this.submitLoading = false;
                }
            },

            async resolveConflicts(){
                this.submitLoading = true;
                try{
                    const conflictObjects = this.getConflictsToResolve();
                    let layer = this.layers.find(layer => layer.layer === this.importObj.filteredType);

                    const dataToSend = {
                      import_uuid: this.importObj.uuid
                    }

                    if (conflictObjects.length > 0) {
                      dataToSend.conflicted_objects = conflictObjects
                    }

                    await this.CREATE_IMPORT({
                        endpoint: layer.endpoint,
                        data: dataToSend
                    });
                    this.clearTable();
                }catch(error){
                    this.$toasted.global.toast_error(error);
                }finally{
                    this.submitLoading = false;
                }
            },

            getConflictsToResolve(){
                const prefix = this.$store.getters.getLayerInitials(this.importObj.filteredType);
                const stringPrefix = `${prefix}_id`;
                const newConflicts = this.getNewConflicts(stringPrefix);
                const conflictedObjects = [];
                Object.entries(newConflicts).forEach(([key, value]) => {
                    conflictedObjects.push({
                        [stringPrefix]: Number(key),
                        conflict: value
                    })
                });
                return conflictedObjects;
            },

            getNewConflicts(stringPrefix){
                const conflicts = this.conflicts.filter(item => this.selectedConflicts.includes(item.new_id));
                const groupedConflicts = conflicts.reduce((acc, item) => {
                    if (!acc[item.new_id]) {
                        acc[item.new_id] = [];
                    }
                    acc[item.new_id].push({[stringPrefix]: item.id});
                    return acc;
                }, {});
                return groupedConflicts;
            },

            resetToDefault(){
                bus.$emit('clear-conflicts');
                bus.$emit('zoom-out-map');
                bus.$emit('clear-vector-source-layer');
                this.conflicts = [];
                this.search = null;
                this.scrollTop = 0;
                this.sortOrder = null;
                this.scrollHeight = 0;
                this.pageNumber = 1;
                this.pagination = 25;
                this.currentPageTop = 1;
                this.currentPageBot = 1;
                this.lastPage = 2;
                this.selectedConflicts = [];
            },

            clearTable(){
                bus.$emit('clear-conflicts');
                bus.$emit('zoom-out-map');
                bus.$emit('clear-vector-source-layer');
                bus.$emit('clear-selected-row');
                this.$emit('clear-selected-import');
                this.conflicts = [];
                this.search = null;
                this.scrollTop = 0;
                this.sortOrder = null;
                this.scrollHeight = 0;
                this.pageNumber = 1;
                this.pagination = 25;
                this.currentPageTop = 1;
                this.currentPageBot = 1;
                this.lastPage = 2;
                this.selectedConflicts = [];
                this.activeTab = 0;
            },

            handleSameRowClick(){
                this.clearTable();
            },

            handleSelectedConflicts(selectedConflicts){
                const allNull = selectedConflicts.every(element => element === null);
                //if all elements are null mens clearTable function was invoked
                if(allNull){
                    return;
                }
                this.selectedConflicts = [...selectedConflicts];
            },

            setActiveTab(index){
                this.activeTab = index;
            },

            // Utility function to remove prefixes from keys in an object
            stripPrefixes(obj) {
                const copy = {...obj};
                const result = {};

                Object.keys(copy).forEach(key => {
                    if (key.includes('_')) {
                        const [prefix, ...rest] = key.split('_');
                        const strippedKey = rest.join('_');
                        result[strippedKey] = copy[key];
                    } else {
                        result[key] = copy[key];
                    }
                });

                return result;
            },

            getInitialConflicts() {
                this.currentPageBot = 1
                this.currentPageTop = 1
                let queryParams = {
                    "page[number]": 1,
                    paginate: 25
                };

                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }

                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllConflicts(queryParams);
            },

            async getAllConflicts(queryParams, scrollDirection = null) {
                try {
                    this.loading = true;
                    const res  = await this.GET_IMPORT_CONFLICTS({
                        queryParams,
                        endpoint: this.importObj.endpoint,
                        uuid: this.importObj.uuid
                    });

                    if(res.data.length === 0){
                        this.$toasted.global.toast_error(this.$i18n.t("NoConflictsMsg"));
                        this.clearTable();
                        return;
                    }

                    const processedConflicts = [];

                    res.data.forEach(element => {
                        const cleanElement = this.stripPrefixes(element);
                        cleanElement.conflict.forEach(conflict => {
                            const cleanConflict = this.stripPrefixes(conflict);
                            processedConflicts.push({
                                ...cleanConflict,
                                geo_json: cleanElement.geo_json,
                                new_id: cleanElement.id
                            })
                        })
                    });
                    res.data = processedConflicts;
                    this.processConflictsData(res, scrollDirection);
                } catch (error) {
                    this.$toasted.global.toast_error(error);
                    this.clearTable();
                }finally {
                    this.loading = false;
                    this.$nextTick(() => this.updateLocalPagination(this.paginationFromParent));
                }
            },

            processConflictsData(conflictsData, scrollDirection = null) {
                let allConflicts = null;
                let currentScroll = null;
                let rowElement = null;
                switch (scrollDirection) {
                    case 'top':
                        this.lastPage = conflictsData.meta.last_page;
                        allConflicts = [...conflictsData.data, ...this.conflicts];
                        this.conflicts = Array.from(new Set(allConflicts.map(a => a.id)))
                            .map(id => { return allConflicts.find(a => a.id === id) })
                        if (this.conflicts.length > 75) {
                            this.conflicts.splice(-(this.conflicts.length - 75));
                            this.currentPageBot--;
                        }

                        currentScroll = document.getElementById(this.tableId).scrollTop;
                        rowElement = document.getElementById("rowId");
                        if (rowElement) {
                            const rowHeight = rowElement.clientHeight;
                            for (let i = 0; i < 25; i++) {
                            const offset = rowHeight * i;
                            document.getElementById(this.tableId).scrollTo({
                                top: currentScroll + offset,
                                behavior: "smooth",
                            });
                            }
                        }
                    break;
                    case 'bottom':
                        this.lastPage = conflictsData.meta.last_page;
                        allConflicts = [...this.conflicts, ...conflictsData.data];
                        this.conflicts = Array.from(new Set(allConflicts.map(a => a.id)))
                            .map(id => { return allConflicts.find(a => a.id === id) })
                        if (this.conflicts.length > 75) {
                            this.conflicts = this.conflicts.slice(this.conflicts.length - 75);
                        }
                        currentScroll = document.getElementById(this.tableId).scrollTop;
                        rowElement = document.getElementById("rowId");
                        if (rowElement) {
                            const rowHeight = rowElement.clientHeight;
                            for (let i = 0; i < 2; i++) {
                            const offset = rowHeight * i;
                            document.getElementById(this.tableId).scrollTo({
                                top: currentScroll - offset,
                                behavior: "smooth",
                            });
                            }
                        }
                    break;
                    default:
                        this.conflicts = conflictsData.data;
                        this.lastPage = conflictsData.meta.last_page;
                    break;
                }
            },

            getConflictsBottom() {
                this.pagination = 25;
                if (this.conflicts.length >= 75) {
                    this.currentPageTop++;
                }
                this.currentPageBot++;

                let queryParams = {
                    "page[number]": this.currentPageBot,
                    paginate: this.pagination,
                };

                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }

                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllConflicts(queryParams, 'bottom');
            },

            async getConflictsTop() {
                this.pagination = 25;
                this.currentPageTop--;

                let queryParams = {
                    "page[number]": this.currentPageTop,
                    paginate: this.pagination,
                };

                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }

                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllConflicts(queryParams, 'top');
            },

            handleSearch: _.debounce(function (event) {
                if (event === "") {
                    this.search = null;
                } else {
                    this.search = event;
                }
                this.conflicts = [];
                this.getInitialConflicts();

            }, 1000),

            onScrollBottom() {
                if (this.currentPageBot !== this.lastPage) {
                    this.pageNumber++;
                    this.getConflictsBottom();
                }
            },

            onScrollTop() {
                if (this.currentPageTop !== 1) {
                    this.pageNumber--;
                    this.getConflictsTop();
                }
            },

            onSortClick(sortOrder, index) {
                this.conflicts = []
                if (sortOrder === "asc") {
                    this.sortOrder = this.headers[index].varName;
                } else if (sortOrder === "desc") {
                    this.sortOrder = `-${this.headers[index].varName}`;
                } else {
                    this.sortOrder = sortOrder;
                }
                this.getInitialConflicts();
            },
        },
    };
</script>

<style scoped>
    .searchContainer {
        display: flex;
        align-items: center;
    }

    .tabsContainer {
        height: calc(8% + 19px);
        display: flex;
        margin-top: 5px;
        border-bottom: 1px solid #DBDBE0;
    }

    .leftCardContainer {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
        padding: 20px 28px;
    }

    .actionsContainer {
        display: flex;
        align-items: center;
        gap: 3px;
    }

    .leftTitle {
        color: #767676;
        font-family: Lato;
        font-size: 10px;
        font-style: normal;
        font-weight: 800;
        line-height: 20px;
        text-transform: uppercase;
    }

    .buttons-container-custom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 20px;
    }

    .cancel-button-custom {
        background: transparent;
        border-radius: 0.25rem;
        border: 0.609px solid var(--app-primary-base);
    }

    .rightSideTitleActions {
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
    .action-buttons {
        margin-top: 3px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        justify-content: flex-end;
        align-items: center;
    }
    .empty-table{
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--app-primary-base);
    }
</style>



// WEBPACK FOOTER //
// src/newComponents/Data/Imports/TableViewConflicts.vue